import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'typed-redux-saga/macro';
import { PayloadAction } from '@reduxjs/toolkit';

import { API_STATUS } from 'src/utils/api-constants';
import { notifyUserByActionTypeAndCode } from 'src/utils/errorHandling/notifications';
import { API_DATE_FORMAT, toApiCallFormat } from 'src/utils/timeUtils';
import { fetchPatientsList } from 'src/redux/data/patient/modules/saga';
import BackendService from 'src/services/BackendService';
import { selectors as loggedInUserSelectors } from 'src/redux/data/loggedInUser';
import { actions as monitorActions } from 'src/redux/data/monitor';
import { loadRoomsList } from 'src/redux/data/rooms/modules/saga';
import { fetchPatientNotes } from 'src/redux/data/notes/modules/saga';
import { UUID } from 'src/types/utility';
import {
  partitionHistogramData,
  processHistogramData,
} from './histogram-processor';
import { actions } from './slice';
import {
  getBedOccupancyInterval,
  getMonitoringDaysTrend,
  mergeHpCustomerFeatureSettings,
} from './utils';
import {
  getTimeFrameByInterval,
  getTimeFrameByRange,
  processBedOccupancyData,
  processMotionData,
  processSleepData,
} from './data-processors';
import { AlertLog } from 'src/types/alerts';
import { getAlertLogsUpdated } from 'src/utils/alertHelpers';
import {
  FetchAlertLogsDataResponse,
  FetchBedOccupancyDataPointCountsResponse,
  FetchMetricHistogramDataResponse,
  FetchMotionIndexMeasurementsResponse,
  FetchVitalSignsMeasurementsResponse,
} from 'src/services/types';
import { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import { MonitoringDaysData } from './types';
import qs from 'qs';

dayjs.extend(minMax);

// eslint-disable-next-line require-yield, @typescript-eslint/no-explicit-any
function* displayErrorNotification(action: PayloadAction<any>) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const data = action.payload;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  notifyUserByActionTypeAndCode(data.actionType, data.deviceId, data.error);
}

function* takeMonitorErrorsActions() {
  yield* all([
    takeLatest(monitorActions.onStartContinuousFail, displayErrorNotification),
    takeLatest(monitorActions.onStopContinuousFail, displayErrorNotification),
    takeLatest(monitorActions.onHRIFailed, displayErrorNotification),
    takeLatest(monitorActions.onInitMonitorFailed, displayErrorNotification),
  ]);
}

function* fetchPatientDeviceInfo(patientId: UUID | null) {
  if (!patientId) {
    return;
  }

  try {
    const { data } = yield* call(
      BackendService.getPatientsLatestSessions,
      patientId,
    );

    const [patientSession] = data.sessions;

    if (!patientSession || patientSession.endTime) {
      yield* put(
        actions.setDeviceInfo({
          deviceId: '',
          deviceConnectionStatus: false,
        }),
      );
      return;
    }

    const deviceStatusReponse = yield* call(
      BackendService.getDeviceById,
      patientSession.deviceId,
    );

    yield* put(
      actions.setDeviceInfo({
        deviceId: patientSession.deviceId,
        deviceConnectionStatus:
          deviceStatusReponse?.data?.data[0]?.connectionStatus?.connected ||
          false,
      }),
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(
      'error in fetching current patient session and device information: ',
      e,
    );
  }
}

function* onLoadIndividualDashboard(
  action: ReturnType<typeof actions.onLoadIndividualDashboard>,
) {
  const patientId = action.payload;
  try {
    yield* put(actions.setIndividualDashboardStatus(API_STATUS.LOADING));
    yield* put(actions.setSelectedPatientId(patientId));
    yield* put(actions.setIndividualDashboardStatus(API_STATUS.OK));

    yield* all([
      call(fetchPatientDeviceInfo, patientId),
      call(fetchPatientsList),
      call(loadRoomsList),
      // @ts-ignore find a way to import other sagas with return typed actions
      call(fetchPatientNotes, { payload: patientId }),
    ]);

    yield* race({
      monitorErrorsActions: call(takeMonitorErrorsActions),
      monitorPageUnmounted: take(actions.individualDashboardUnmounted),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('error in onLoadIndividualDashboard: ', e);
    yield* put(actions.loadIndividualDashboardFailed());
  }
}

export function* getMonitoringDaysData(
  action: ReturnType<typeof actions.getMonitoringDaysData>,
) {
  const { patientId, currentMonth } = action.payload;
  try {
    // "previousMonthResponse" is required only for computing the "trend" value
    const [currentMonthResponse, previousMonthResponse] = yield* all([
      call(BackendService.getMonitoringDaysData, {
        patientId,
        startTime: dayjs(currentMonth).startOf('month').format(API_DATE_FORMAT),
        endTime: dayjs(currentMonth).endOf('month').format(API_DATE_FORMAT),
      }),
      call(BackendService.getMonitoringDaysData, {
        patientId,
        startTime: dayjs(currentMonth)
          .subtract(1, 'month')
          .startOf('month')
          .format(API_DATE_FORMAT),
        endTime: dayjs(currentMonth)
          .subtract(1, 'month')
          .endOf('month')
          .format(API_DATE_FORMAT),
      }),
    ]);

    const currentMonthData = currentMonthResponse?.data;
    const previousMonthData = previousMonthResponse?.data;
    const trend =
      Number.isInteger(currentMonthData?.monitoredDaysCount) &&
      Number.isInteger(currentMonthData?.totalDays) &&
      Number.isInteger(previousMonthData?.monitoredDaysCount) &&
      Number.isInteger(previousMonthData?.totalDays)
        ? getMonitoringDaysTrend(
            currentMonthData as MonitoringDaysData,
            previousMonthData as MonitoringDaysData,
          )
        : undefined;

    if (currentMonthData) {
      yield* put(
        actions.loadMonitoringDaysDataSuccess({
          data: {
            ...currentMonthData,
            trend,
          },
        }),
      );
    } else {
      yield* put(
        actions.loadMonitoringDaysDataSuccess({
          data: null,
        }),
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('error in getMonitoringDaysData: ', e);
    yield* put(actions.loadMonitoringDaysDataFailed());
  }
}

export function* getSleepData(action: ReturnType<typeof actions.getSleepData>) {
  const { startTime: defaultStartTime, endTime: defaultEndTime } =
    getBedOccupancyInterval();

  const {
    patientId,
    startTime = defaultStartTime,
    endTime = defaultEndTime,
  } = action.payload;

  const customerId = yield* select(
    loggedInUserSelectors.getLoggedInUserCustomerId,
  );
  const loggedInTenantId = yield* select(loggedInUserSelectors.getUserTenantId);
  const queryParams = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const impersonatedSubtenantId = queryParams.impersonatedSubtenantId as string;
  const siteId = impersonatedSubtenantId || loggedInTenantId;
  if (!customerId || !siteId || !startTime || !endTime) {
    console.error('error in getSleepData - no customerId or siteId provided ');
    return;
  }
  yield* put(
    actions.setSleepDataStatus({ patientId, status: API_STATUS.LOADING }),
  );
  try {
    const { data } = yield* call(BackendService.getSeepData, {
      customerId,
      siteId,
      patientId,
      startTime,
      endTime,
    });
    yield* put(
      actions.loadSleepDataSuccess(processSleepData(data.sleepData, patientId)),
    );
    yield* put(
      actions.setSleepDataStatus({ patientId, status: API_STATUS.OK }),
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('error in getSleepData: ', e);
    yield* put(
      actions.setSleepDataStatus({ patientId, status: API_STATUS.ERROR }),
    );
  }
}
export function* getMotionData(
  action: ReturnType<typeof actions.getMotionData>,
) {
  const { startTime: defaultStartTime, endTime: defaultEndTime } =
    getBedOccupancyInterval();

  const {
    patientId,
    startTime = defaultStartTime,
    endTime = defaultEndTime,
  } = action.payload;
  if (!startTime || !endTime || !patientId) {
    return;
  }
  yield* put(
    actions.setMotionDataStatus({ patientId, status: API_STATUS.LOADING }),
  );
  try {
    const { data } = yield* call(BackendService.getMotionAggregatedData, {
      patientId,
      startTime,
      endTime,
    });
    yield* put(
      actions.loadMotionDataSuccess(
        processMotionData(data.motionDailyResponse, patientId),
      ),
    );
    yield* put(
      actions.loadBedOccupancyDataSuccess(
        processBedOccupancyData(data.motionDailyResponse, patientId),
      ),
    );
    yield* put(
      actions.setMotionDataStatus({ patientId, status: API_STATUS.OK }),
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('error in getMotionData: ', e);
    yield* put(
      actions.setMotionDataStatus({ patientId, status: API_STATUS.ERROR }),
    );
  }
}
export function* getHpAvailableFeatures() {
  const customerId = yield* select(
    loggedInUserSelectors.getLoggedInUserCustomerId,
  );
  if (!customerId) {
    console.error('error in getAvailableFeatures - no customerId provided ');
    return;
  }
  yield* put(actions.setAvailableFeaturesStatus(API_STATUS.LOADING));
  try {
    const { data } = yield* call(BackendService.getAvailableFeatures, {
      customerId,
    });
    const hpFeatureSettings = mergeHpCustomerFeatureSettings(data);
    if (!hpFeatureSettings) {
      throw new Error(`Invalid feature settings for customer ${customerId}`);
    }
    yield* put(actions.setAvailableFeatures(hpFeatureSettings));
    yield* put(actions.setAvailableFeaturesStatus(API_STATUS.OK));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('error in setAvailableFeaturesStatus: ', e);
    yield* put(actions.setAvailableFeaturesStatus(API_STATUS.ERROR));
  }
}
export function* getSiteFeatureSettings(
  action: ReturnType<typeof actions.getSiteFeatureSettings>,
) {
  const { featureName = 'SLEEP_WAKE' } = action.payload;
  const loggedInTenantId = yield* select(loggedInUserSelectors.getUserTenantId);
  const queryParams = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  const impersonatedSubtenantId = queryParams.impersonatedSubtenantId as string;
  const siteId = impersonatedSubtenantId || loggedInTenantId;
  if (!siteId) {
    console.error('error in getHpAvailableFeatures - no siteId provided ');
    return;
  }
  yield* put(actions.setSiteFeatureSettingsStatus(API_STATUS.LOADING));
  try {
    const { data } = yield* call(BackendService.getSiteFeatureSettings, {
      siteId,
      featureName,
    });
    yield* put(actions.setSiteFeatureSettings({ [data.featureName]: data }));
    yield* put(actions.setSiteFeatureSettingsStatus(API_STATUS.OK));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('error in getSiteFeatureSettings: ', e);
    yield* put(actions.setSiteFeatureSettingsStatus(API_STATUS.ERROR));
  }
}

function* getHistogramData(
  action: ReturnType<typeof actions.getHistogramData>,
) {
  const { patientId, startTime, endTime } = action.payload;
  const startTimePreviousWeek = startTime.subtract(1, 'week');
  const endTimePreviousWeek = startTime.subtract(1, 'second');

  yield* put(actions.getHistogramDataRequest());

  try {
    const [mainDataResponse, previousWeekDataResponse] = yield* all([
      call(BackendService.getHistogramData, {
        patientId,
        startTime: startTime.format(API_DATE_FORMAT),
        endTime: endTime.format(API_DATE_FORMAT),
      }),
      call(BackendService.getHistogramData, {
        patientId,
        startTime: startTimePreviousWeek.format(API_DATE_FORMAT),
        endTime: endTimePreviousWeek.format(API_DATE_FORMAT),
      }),
    ]);

    const rawMainData = (
      mainDataResponse as AxiosResponse<
        FetchMetricHistogramDataResponse,
        unknown
      >
    ).data;
    const rawPreviousWeekData = (
      previousWeekDataResponse as AxiosResponse<
        FetchMetricHistogramDataResponse,
        unknown
      >
    ).data;

    yield* put(
      actions.getHistogramDataSuccess(
        partitionHistogramData({
          mainData: processHistogramData(rawMainData),
          previousWeekData: processHistogramData(rawPreviousWeekData),
        }),
      ),
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('An error occurred when getting histogram data', e);
  }
}

export function* getAlertsLogData(
  action: ReturnType<typeof actions.getAlertsLogData>,
) {
  const { patientId, startDate, endDate, currentPage, pageSize } =
    action.payload;
  try {
    yield* put(actions.setAlertsLogStatus(API_STATUS.LOADING));
    const { data } = yield* call(BackendService.getAlertsLogData, {
      patientId,
      startDate: toApiCallFormat(startDate),
      endDate: toApiCallFormat(endDate),
      page: currentPage,
      limit: pageSize,
    });
    const timezone = yield* select(loggedInUserSelectors.getUserTenantTimezone);
    const updatedAlertLogs = getAlertLogsUpdated(
      data.alertLogs,
      timezone,
    ) as AlertLog[];

    yield* put(
      actions.setAlertsLogData({
        alerts: updatedAlertLogs,
        pageMetadata: data.metadata.page,
      }),
    );
    yield* put(actions.setAlertsLogStatus(API_STATUS.OK));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('error in getAlertsLogData: ', e);
    yield* put(actions.setAlertsLogStatus(API_STATUS.ERROR));
    yield* put(actions.setIndividualDashboardStatus(API_STATUS.ERROR));
  }
}

export function* getAlertsLogDataForModal(
  action: ReturnType<typeof actions.getAlertsLogData>,
) {
  const { patientId, startDate, endDate, currentPage, pageSize } =
    action.payload;
  try {
    yield* put(actions.setAlertsLogForModalStatus(API_STATUS.LOADING));
    const { data } = yield* call(BackendService.getAlertsLogData, {
      patientId,
      startDate: toApiCallFormat(startDate),
      endDate: toApiCallFormat(endDate),
      page: currentPage,
      limit: pageSize,
    });
    const timezone = yield* select(loggedInUserSelectors.getUserTenantTimezone);
    const updatedAlertLogs = getAlertLogsUpdated(
      data.alertLogs,
      timezone,
    ) as AlertLog[];

    yield* put(
      actions.setAlertsLogDataForModal({
        alerts: updatedAlertLogs,
        pageMetadata: data.metadata.page,
      }),
    );
    yield* put(actions.setAlertsLogForModalStatus(API_STATUS.OK));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('error in getAlertsLogData: ', e);
    yield* put(actions.setAlertsLogForModalStatus(API_STATUS.ERROR));
    yield* put(actions.setIndividualDashboardStatus(API_STATUS.ERROR));
  }
}

function* fetchPatientMetricDataPointsByDateTime(
  action: ReturnType<typeof actions.getPatientMetricData>,
) {
  const { patientId, startTime, endTime } = action.payload;
  try {
    yield* put(actions.setAlertsLogStatus(API_STATUS.LOADING));
    const [
      measurements,
      motionResponse,
      alertsLogResponse,
      bedOccupanciesResponse,
    ] = yield* all([
      call(BackendService.getAllGraphDataByDateTime, {
        patientId,
        startTime: startTime.format(API_DATE_FORMAT),
        endTime: endTime.format(API_DATE_FORMAT),
      }),
      call(BackendService.getGraphDataMotionIndexByDateTime, {
        patientId,
        startTime: startTime.format(API_DATE_FORMAT),
        endTime: endTime.format(API_DATE_FORMAT),
      }),
      call(BackendService.getAlertsLogData, {
        patientId,
        startDate: startTime.format(API_DATE_FORMAT),
        endDate: endTime.format(API_DATE_FORMAT),
      }),
      call(BackendService.getBedOccupancyDataPointCounts, {
        patientId,
        startTime: startTime.format(API_DATE_FORMAT),
        endTime: endTime.format(API_DATE_FORMAT),
      }),
    ]);

    const timezone = (yield* select(
      loggedInUserSelectors.getUserTenantTimezone,
    )) as unknown as string;
    const alertLogsData = (
      alertsLogResponse as AxiosResponse<FetchAlertLogsDataResponse, unknown>
    )?.data?.alertLogs;
    const alertLogsMetadata = (
      alertsLogResponse as AxiosResponse<FetchAlertLogsDataResponse, unknown>
    )?.data?.metadata;
    const updatedAlertLogs = getAlertLogsUpdated(
      alertLogsData,
      timezone,
    ) as AlertLog[];

    yield* put(
      actions.setAlertsLogData({
        alerts: updatedAlertLogs,
        pageMetadata: alertLogsMetadata.page,
      }),
    );
    yield* put(actions.setAlertsLogStatus(API_STATUS.OK));
    const motionData = motionResponse
      ? (
          motionResponse as AxiosResponse<
            FetchMotionIndexMeasurementsResponse,
            unknown
          >
        ).data
      : undefined;
    const measurementsData = measurements
      ? (
          measurements as AxiosResponse<
            FetchVitalSignsMeasurementsResponse,
            unknown
          >
        ).data
      : undefined;
    yield* put(
      actions.getPatientMetricDataSuccess({
        startTime,
        endTime,
        measurementsData,
        motionData: motionData?.motion_aggregation,
        positionData: motionData?.posture_count,
        alertLogsData,
        bedOccupancyDataPointsCounts: (
          bedOccupanciesResponse as AxiosResponse<
            FetchBedOccupancyDataPointCountsResponse,
            unknown
          >
        ).data.bedOccupancies,
        timezone,
      }),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `An error occurred while trying to fetch metric graph points for patient id: ${patientId} from date: ${startTime.format()} to date: ${endTime.format()} .\n Error:`,
      error,
    );
    yield* put(actions.setAlertsLogStatus(API_STATUS.ERROR));
    yield* put(actions.getPatientMetricDataFailure());
  }
}

export function* changeTimeFrame(
  action: ReturnType<typeof actions.changeTimeFrame>,
) {
  const { range, startTime, endTime } = action.payload;
  const timeFrame = range
    ? getTimeFrameByRange(range)
    : getTimeFrameByInterval(startTime, endTime);

  yield* put(actions.setSelectedTimeFrame(timeFrame));
}

export default function* watchIndividualDashboardActions() {
  yield* all([
    takeLatest(actions.onLoadIndividualDashboard, onLoadIndividualDashboard),
    takeLatest(actions.getHistogramData, getHistogramData),
    takeLatest(actions.getMonitoringDaysData, getMonitoringDaysData),
    takeLatest(
      actions.getPatientMetricData,
      fetchPatientMetricDataPointsByDateTime,
    ),
    takeLatest(actions.getAlertsLogData, getAlertsLogData),
    takeLatest(actions.getAlertsLogDataForModal, getAlertsLogDataForModal),
    takeLatest(actions.changeTimeFrame, changeTimeFrame),
    takeEvery(actions.getSleepData, getSleepData),
    takeEvery(actions.getMotionData, getMotionData),
    takeLatest(actions.getHpAvailableFeatures, getHpAvailableFeatures),
    takeLatest(actions.getSiteFeatureSettings, getSiteFeatureSettings),
  ]);
}
